import React from 'react';
import { connect } from 'react-redux';
import {
	Box,
	Flex,
	Link,
	PrimaryButton,
	Text,
	CheckmarkIllustration,
} from '@stashinvest/ui';

import {
	confirmDebitEnrollment,
	postAccount,
	postAccountSubmit,
	getEsigAgreements,
	openDialog,
} from 'stash/actions';
import {
	PERSONAL_BROKERAGE,
	ROTH_IRA,
	TRADITIONAL_IRA,
} from 'stash/constants/accountTypes';
import { SignUpStep } from 'stash/components/sign-up/SignUpScreenLayout';
import { selectAccount } from 'stash/selectors';
import { trackEvent } from 'stash/actions/tracking';
import { BanjoString } from '@stashinvest/react-banjo';

const contactUsMsg = (accountType) => {
	return `Sorry...we had an issue creating your ${accountType} account. Please email customer support at support@stash.com.`;
};

export const ESigDocument = (props) => {
	return (
		<Flex flexDirection="row">
			<Box mb="s" key={props.title}>
				<Link href={props.link} target="_blank" rel="noopener noreferrer">
					{props.title}
				</Link>
				<Text.BodySmall>{props.description}</Text.BodySmall>
			</Box>
		</Flex>
	);
};

export const mapStateToProps = (state, ownProps) => ({
	investAccountId: state.entities.account_by_type.PERSONAL_BROKERAGE,
	investAccount: selectAccount(state, PERSONAL_BROKERAGE),
	retireAccountId:
		selectAccount(state, ROTH_IRA) || selectAccount(state, TRADITIONAL_IRA),
	agreementsData: state.entities.agreements,
	retireAccountType: state?.application?.signUp?.data?.retireAccountType ?? ROTH_IRA,
	profile: state.entities.profile,
	userId: state.entities.user.uuid,
	platform_tier: state.entities.user.platform_tier,
});

export const mapDispatchToProps = (dispatch, ownProps) => ({
	submitAccount: (data) => {
		return dispatch(postAccount(data));
	},
	submitAccountToApex: (accountId) => {
		return dispatch(postAccountSubmit(accountId, { e_signature_agreed: true }));
	},
	fetchMultiEsigAgreements: (accountTypes) => {
		return dispatch(getEsigAgreements(accountTypes));
	},
	confirmEnrollment: (address) => {
		return dispatch(confirmDebitEnrollment(address));
	},
	showDialog: (msg, primaryCta, secondaryCta) => {
		return dispatch(openDialog(msg, primaryCta, secondaryCta)).catch(() => {});
	},
	sendEvent: (event, properties) => {
		return dispatch(trackEvent(event, properties));
	},
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
	return {
		...stateProps,
		...dispatchProps,
		...ownProps,
		onSubmit: async (isBankEligible) => {
			const {
				investAccountId,
				investAccount,
				retireAccountId,
				retireAccountType,
				platform_tier,
			} = stateProps;

			const { submitAccount, submitAccountToApex, showDialog, sendEvent } = dispatchProps;

			const productTypes = platform_tier.product_types.reduce((acc, type) => {
				return {
					...acc,
					[type]: true,
				};
			}, {});

			sendEvent('VerifyAccount', {
				ScreenName: 'ESigAgree',
				Action: 'AgreeContinue',
			});

			// Create invest account and submit to APEX.
			if (!investAccountId && productTypes[PERSONAL_BROKERAGE]) {
				let investAccountResponse;

				try {
					investAccountResponse = await submitAccount({
						account: {
							account_type: PERSONAL_BROKERAGE,
							e_signature_agreed: true,
						},
					});
				} catch {
					await showDialog({
						prompt: contactUsMsg('Stash invest'),
						confirmText: 'Okay',
					});
				}

				try {
					await submitAccountToApex(investAccountResponse.account.id);
				} catch {
					await showDialog({
						prompt: contactUsMsg('invest'),
						confirmText: 'Okay',
					});
				}
			}

			// Submit to APEX if invest account exists but wasn't submitted.
			if (investAccountId && !investAccount.e_signature_agreed_at) {
				try {
					await submitAccountToApex(investAccountId);
				} catch {
					await showDialog({
						prompt: contactUsMsg('invest'),
						confirmText: 'Okay',
					});
				}
			}

			// Create retire account and submit to APEX.
			if (!retireAccountId && productTypes.IRA) {
				let retireAccountResponse;

				try {
					retireAccountResponse = await submitAccount({
						account: {
							account_type: retireAccountType,
							e_signature_agreed: true,
						},
					});
				} catch {
					return showDialog({
						prompt: contactUsMsg('Stash retire'),
						confirmText: 'Okay',
					});
				}

				try {
					await submitAccountToApex(retireAccountResponse.account.id);
				} catch {
					return showDialog({
						prompt: contactUsMsg('retire'),
						confirmText: 'Okay',
					});
				}
			}

			ownProps.completeStep({ canReturn: false });
		},
	};
};

export class SignUpESign extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			ready: false,
			showAll: false,
			checked: true,
			isBankEligible: false,
			isSubmitting: false,
			disclosureAgreements: {},
		};
	}

	componentDidMount() {
		const {
			confirmEnrollment,
			fetchMultiEsigAgreements,
			retireAccountType,
			profile,
			sendEvent,
		} = this.props;

		const address = {
			addressLine1: profile?.home_street_address?.[0],
			addressLine2: profile?.home_street_address?.[1],
			city: profile?.home_city,
			state: profile?.home_state,
			postalCode: profile?.home_postal_code,
		};

		sendEvent('VerifyAccount', {
			ScreenName: 'ESigAgree',
		});

		fetchMultiEsigAgreements([retireAccountType]).then((data) => {
			this.setState({ ready: true, disclosureAgreements: data });
		});

		// Check the user's bank eligibility through their address to
		// determine if we show the bank agreement
		confirmEnrollment(address)
			.then((res) => {
				this.setState({ isBankEligible: true });
			})
			.catch((error) => {
				this.setState({ isBankEligible: false });
			});
	}

	render() {
		const { agreementsData, onSubmit, platform_tier } = this.props;
		const { isBankEligible, isSubmitting, checked } = this.state;

		const hasInvestProduct = platform_tier?.product_types?.includes('PERSONAL_BROKERAGE');

		const renderBankAgreementSection = () => {
			return (
				<Text mb="m">
					<BanjoString stringId="information/disclose/genericAccountAgreements" />
				</Text>
			);
		};

		const renderEsigAgreement = () => {
			return (
				<SignUpStep>
					<Box maxWidth="28rem" width="100%" pt="2rem">
						<CheckmarkIllustration
							alt="agreement"
							size="120px"
							display="block"
							mx="auto"
							mb="s"
						/>
						<Text.H1 size="3" mb="xxs">
							<BanjoString stringId="information/instruct/stashAccountAgreements" />
						</Text.H1>
						<Box className="signup__content">
							<Text mb="m">
								<BanjoString stringId="information/giveContext/stashAccountAgreements" />
							</Text>
							<Box mb="s">
								<Text mb="m" fontWeight="bold">
									<BanjoString stringId="management/property/investingTerms" />
								</Text>
								<Flex alignItems="left" flexDirection="column">
									{agreementsData?.all_links.map((document, idx) => (
										<ESigDocument
											key={idx}
											title={document.title}
											link={document.link}
											description={document.description}
										/>
									))}
								</Flex>
							</Box>
							{isBankEligible && hasInvestProduct ? renderBankAgreementSection() : ''}
							<Flex alignItems="center" flexDirection="column">
								<PrimaryButton
									type="submit"
									mb="m"
									disabled={!checked}
									loading={isSubmitting}
									onClick={async () => {
										this.setState({ isSubmitting: true });
										await onSubmit(isBankEligible);
										this.setState({ isSubmitting: false });
									}}
								>
									<BanjoString stringId="action/executeTask/agreeToTerms" />
								</PrimaryButton>
							</Flex>
						</Box>
					</Box>
				</SignUpStep>
			);
		};

		const renderLoader = () => <div />;
		return this.state.ready ? renderEsigAgreement() : renderLoader();
	}
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(SignUpESign);
